/**
 * Classify a datasource action by its SQL STATEMENT — never by its name.
 *
 * ## Why this exists
 *
 * The MCP surface had ~18 places that answered "is this action a mutation?"
 * with a regex on the action NAME, all shaped after the designer's generated
 * CRUD naming (`<Entity>_Insert`, `<Entity>_Update`). Custom SQL actions are
 * authored verb-first (`CreateExpense`, `SubmitExpense`, `ApproveExpense`), so
 * every one of them slipped through: the pageInit mutation guard did not fire,
 * the insert PK never received `NewGuid`, and the post-build auto-bind passes
 * processed zero actions while reporting a clean run (KOA-18227, measured on
 * the masraf build).
 *
 * The statement is the fact. `INSERT` / `UPDATE` / `DELETE` / `MERGE` /
 * `TRUNCATE` means the action writes, regardless of what it is called or which
 * language it is named in.
 *
 * ## Keep in sync
 *
 * `mcp-chat-server/src/types/action-types.ts` — `classifyActionKindFromSql`,
 * `classifyMutationShapeFromSql`. The browser cannot import that module; the
 * two implementations must agree, same contract as `USER_FK_NAME_PATTERN` ↔
 * `current-user-defaults.ts`. Change one, change both.
 *
 * That agreement is no longer left to memory (KOA-18158): both implementations
 * are asserted against ONE shared table of statements,
 * `mcp-chat-server/src/types/__tests__/fixtures/sql-classification-cases.json`
 * — by `sql-classification-parity.test.ts` there and by this module's own test
 * here, so agreement is transitive and a drift fails a suite. A new statement
 * shape belongs in that JSON, never copied into either suite.
 *
 * ## Deliberately NOT using `statementType`
 *
 * `IActionDefinitionDto.statementType` (`EStatementTypes`) is the platform's own
 * label, but it cannot answer this question: a custom action that writes and
 * returns rows (`INSERT ... OUTPUT INSERTED.Id`) is a `CustomExecuteReader`,
 * which reads as "query". Any write keyword outweighs the presence of a SELECT.
 */

/** Read vs. write. `null` = no statement to judge. */
export type SqlStatementKind = "query" | "mutation";

/** Which write. `null` = a query, or a write whose verb could not be identified. */
export type SqlMutationShape = "insert" | "update" | "delete";

/** Data-modifying keywords — shared by both classifications. */
const WRITE_KEYWORD_PATTERN: RegExp = /\b(INSERT|UPDATE|DELETE|MERGE|TRUNCATE)\b/;

/**
 * Upper-cased statement with the regions that must NOT be keyword-scanned
 * removed: block comments, line comments, and single-quoted string literals.
 *
 * A status literal spelled `'DELETED'`, a commented-out UPDATE, or a Turkish
 * comment mentioning `insert` would otherwise make a pure SELECT read as a
 * write. Bracket-quoted identifiers are left alone — `[Update]` is still an
 * identifier, and stripping brackets would hide real keywords.
 *
 * Returns `""` when there is nothing to classify, so callers can treat empty as
 * "no evidence" rather than as a negative answer.
 */
function sqlKeywordScope(statement: string | undefined | null): string {
  if (typeof statement !== "string" || statement.trim().length === 0) return "";
  return statement
    .replace(/\/\*[\s\S]*?\*\//g, " ")
    .replace(/--[^\n\r]*/g, " ")
    .replace(/'(?:[^']|'')*'/g, " ")
    .toUpperCase();
}

/** Read vs. write from the statement. `null` when there is no statement to judge. */
export function classifySqlStatementKind(statement: string | undefined | null): SqlStatementKind | null {
  const scannable: string = sqlKeywordScope(statement);
  if (scannable.length === 0) return null;
  if (WRITE_KEYWORD_PATTERN.test(scannable)) return "mutation";
  if (/\bSELECT\b/.test(scannable)) return "query";
  return null;
}

/**
 * Which write the statement performs. Precedence INSERT → UPDATE/MERGE →
 * DELETE: an upsert is shaped `insert` because its primary key may have to be
 * minted, and shaping it `update` would leave the PK unbound on the insert
 * branch.
 */
export function classifySqlMutationShape(statement: string | undefined | null): SqlMutationShape | null {
  const scannable: string = sqlKeywordScope(statement);
  if (scannable.length === 0) return null;
  if (/\bINSERT\b/.test(scannable)) return "insert";
  if (/\b(UPDATE|MERGE)\b/.test(scannable)) return "update";
  if (/\b(DELETE|TRUNCATE)\b/.test(scannable)) return "delete";
  return null;
}

/**
 * True when the statement is known to write. A `false` here means "no write
 * evidence in the SQL", which for an action with no statement at all (a
 * built-in, a Swagger/SAP/C# action) is NOT the same as "safe to treat as a
 * read" — callers that need a safety decision must keep their own fallback.
 */
export function sqlStatementWrites(statement: string | undefined | null): boolean {
  return classifySqlStatementKind(statement) === "mutation";
}
